import React, { useEffect, useState } from 'react';
import { Button, message } from 'antd';
import { PlusOutlined, WarningOutlined } from '@ant-design/icons';
import ContentModal from './ContentModal';
import IssueContentMeta from './IssueContentMeta';
import IssueFormModal from './IssueFormModal';
import IssuesTable from './IssuesTable';
import PanelFrame from './PanelFrame';
import { useContentViewer } from './useContentViewer';
import type { PanelChrome } from './layout';
import {
	useDeleteProgramIssueMutation,
	useGetProgramIssuesQuery,
	useGetProgramQuery,
} from '../../redux/api/manufacturing';
import type { ProgramIssue } from '../../interfaces/CamProgramming.interface';

interface IssuesPanelProps {
	programId: string;
	/** When set the panel only shows and creates issues raised inside this review. */
	reviewId?: string;
	title?: string;
	chrome?: PanelChrome;
}

const IssuesPanel: React.FC<IssuesPanelProps> = ({
	programId,
	reviewId,
	title = 'Reported Issues',
	chrome,
}) => {
	const { data, isLoading } = useGetProgramIssuesQuery({
		program: programId,
		...(reviewId ? { review: reviewId } : {}),
	});
	// Lets the fault column name the person on the hook rather than the role.
	const { data: program } = useGetProgramQuery(programId);
	const [deleteIssue] = useDeleteProgramIssueMutation();

	const [modalOpen, setModalOpen] = useState(false);
	const [editingIssue, setEditingIssue] = useState<ProgramIssue | null>(null);
	const viewer = useContentViewer<ProgramIssue>();

	const issues = data?.results || [];

	useEffect(() => {
		setModalOpen(false);
		setEditingIssue(null);
	}, [programId, reviewId]);

	const handleOpenCreate = () => {
		setEditingIssue(null);
		setModalOpen(true);
	};

	const handleOpenEdit = (issue: ProgramIssue) => {
		setEditingIssue(issue);
		setModalOpen(true);
	};

	const handleDelete = async (issue: ProgramIssue) => {
		try {
			await deleteIssue({ id: issue.id, programId }).unwrap();
			message.success('Issue removed');
		} catch {
			message.error('Failed to remove issue');
		}
	};

	return (
		<>
			<PanelFrame
				chrome={chrome}
				icon={<WarningOutlined className="cam-section__icon" />}
				title={title}
				count={issues.length}
				actionPlacement="below"
				action={(expand) => (
					<Button
						icon={<PlusOutlined />}
						onClick={() => {
							expand();
							handleOpenCreate();
						}}
					>
						Log Issue
					</Button>
				)}
			>
				<IssuesTable
					issues={issues}
					loading={isLoading}
					showSource={!reviewId}
					program={program}
					onView={viewer.view}
					onEdit={handleOpenEdit}
					onDelete={handleDelete}
				/>
			</PanelFrame>

			<IssueFormModal
				programId={programId}
				issue={editingIssue}
				reviewId={reviewId ?? null}
				open={modalOpen}
				onClose={() => setModalOpen(false)}
			/>

			<ContentModal
				title="Issue"
				meta={
					viewer.entry && (
						<IssueContentMeta issue={viewer.entry} program={program} />
					)
				}
				html={viewer.entry?.description ?? null}
				emptyText="No description"
				open={viewer.open}
				onClose={viewer.close}
				afterClose={viewer.afterClose}
			/>
		</>
	);
};

export default IssuesPanel;
